import './index.scss'
import text from './text.json'
import currencies from './../../data/currencies.json'
import ResponsivePageTitle from '../../components/core/title/ResponsivePageTitle'
import {PageSubTitle, PageTitle} from '../../components/core/title'
import Footer from '../../components/layouts/footer'
import ContentLoader from '../../components/core/loader'
import Table from '../../components/core/table'
import EmptyContent from '../resources/EmptyContent'
import {
    createSubscriptionSession,
    deleteSubscription,
    getSubscriptionPayments,
    getSubscriptions,
    reactivateSubscription
} from '../../stores/payment'
import {useDispatch, useSelector} from 'react-redux'
import {useEffect, useRef, useState} from 'react'
import TextHelper from './../../utils/text'
import AmountHelper from './../../utils/amount'
import {ArrowDownIcon, FileIcon, InfoIcon, WarningIcon} from '../../icons'
import {setConfirmIsActive} from '../../stores/alert'
import Confirm from '../../components/core/confirm'
import {SmallButton} from '../../components/core/button'
import ButtonLoader from '../../components/core/button/ButtonLoader'
import InputInfo from '../../components/core/inputInfo'
import useOutsideClick from './../../utils/hooks/useOutsideClick'
import FullscreenLoader from '../../components/core/loader/fullcreenLoader'
import moment from 'moment/moment'
import {Link, useNavigate} from 'react-router-dom'
import {routes} from '../../routes'
import LimitManager from '../../utils/limitManager'
import TrialPeriodContent from './TrialPeriodContent'
import Customer from '../../utils/customer'
import TagManager from 'react-gtm-module'
import tagManager from '../../data/tagManager.json'
import {colors} from '../../utils/colors'
import classNames from 'classnames'
import plansData from '../../data/plans.json'
import localStorageKeys from '../../utils/localStorageKeys'
import localStorageManager from '../../utils/localStorageManager'

const Subscriptions = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const subscriptions = useSelector(state => state.payment.subscriptions)
    const paymentData = useSelector(state => state.payment.paymentData)
    const isFetching = useSelector(state => state.payment.isFetching)
    const isDeleting = useSelector(state => state.payment.isDeleting)
    const isPaymentFetching = useSelector(state => state.payment.isPaymentFetching)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const [paymentId, setPaymentId] = useState('')
    const [processingId, setProcessingId] = useState(null)
    const [showCanceledInfo, setShowCanceledInfo] = useState('')
    const [showLoader, setShowLoader] = useState(false)
    const [showUpgradePlan, setShowUpgradePlan] = useState(false)
    const [deletingSubscriptionId, setDeletingSubscriptionId] = useState('')
    const isTrialExpired = new Date(localStorageManager.get(localStorageKeys.TRIAL)?.expires) < new Date()
    const isTrialPeriod = LimitManager.isTrialPeriod() && !isTrialExpired
    const infoBoxRef = useRef()
    const tableHeader = [
        {
            label: 'Purpose',
            className: 'min-width-250'
        },
        {
            label: 'Amount',
            className: 'min-width'
        },
        {
            label: 'Paid date',
            className: 'min-width'
        },
        {
            label: 'Interval',
            className: 'min-width'
        },
        {
            label: 'Invoice',
            className: 'min-width'
        }
    ]

    const tbodyData = []

    useOutsideClick(infoBoxRef, () => setShowCanceledInfo(''))

    const keyToNamePlan = plan => {
        return Object.values(plansData).find(planData => planData.key === plan)?.display_name
    }

    const getPayments = id => {
        (paymentId !== id && paymentData?.data?.subscription?.subscription_id !== id) && dispatch(getSubscriptionPayments(id))
        paymentId === id ? setPaymentId('') : setPaymentId(id)
    }

    const returnVisitUrl = url => {
        return <span className="min-width">
            <a className="show-invoice" href={url} target='_blank'>
                <FileIcon className="table-icon"/>
                <span>{text.show}</span>
            </a>
        </span>
    }

    const returnPrice = (amount, currency) => {
        return <span className="min-width">
            {AmountHelper.normalizePrice(amount, currencies[currency])}
        </span>
    }

    const checkIsIdentificContent = id => {
        return paymentId === id
    }

    const cancelSubscription = async() => {
        dispatch(setConfirmIsActive(false))
        const success = await dispatch(deleteSubscription(deletingSubscriptionId))
        if (success) {
            dispatch(getSubscriptions())
            TagManager.dataLayer({
                dataLayer: {
                    event: tagManager.events.formSubmission,
                    formAction: tagManager.formActions.deny_trial
                }
            })
        }
    }

    const activateSubscription = async(id) => {
        setProcessingId(id)
        const success = await dispatch(reactivateSubscription(id))
        setProcessingId(null)
        if (success) {
            dispatch(getSubscriptions())
        }
    }

    const triggerCreateSubscriptionSession = id => {
        setShowLoader(true)
        dispatch(createSubscriptionSession(setShowLoader, id))
    }

    const toggleInfo = id => {
        showCanceledInfo === id ? setShowCanceledInfo('') : setShowCanceledInfo(id)
    }

    const getEmptyContent = () => {
        if (isTrialPeriod) {
            return <TrialPeriodContent isDarkMode={isDarkMode}/>
        }
        return (
            <EmptyContent
                message={text?.no_subscription}
                hideButton={false}
                buttonText={text.plans}
                showIcon={false}
                onclick={() => navigate(routes.plans.path)}
            />
        )
    }

    useEffect(() => {
        dispatch(getSubscriptions(true))
    }, [])

    useEffect(() => {
        subscriptions?.data?.subscriptions?.every(item => item?.active === false) && setShowUpgradePlan(true)
    }, [subscriptions])

    paymentData?.data?.subscription?.payments.map(payment => {
        tbodyData.push([
            <span className="min-width-250 payment-desc">{payment?.description}</span>,
            returnPrice(payment?.paid_amount, payment?.currency),
            <span className="min-width">{moment(payment?.paid_at).format('YYYY-MM-DD HH:mm')}</span>,
            <span className="min-width">{TextHelper.capitalizeFirstChar(payment?.plan_interval)}</span>,
            returnVisitUrl(payment?.hosted_invoice_url)
        ])
    })

    return (
        <>
            {showLoader && <FullscreenLoader/>}
            <main className={classNames('main subscriptions', {
                dark: isDarkMode
            })}>
                <Confirm
                    body={text.are_you_sure}
                    callback={cancelSubscription}
                    showLoader={isDeleting}
                />
                <div>
                    <ResponsivePageTitle/>
                    <div className="content-title">
                        <div className="left-side">
                            <div>
                                <PageSubTitle>
                                    <p className={classNames('section', {
                                        dark: isDarkMode
                                    })}>{text.section}</p>
                                </PageSubTitle>
                                <PageTitle>
                                    <h1  className={classNames('page-title', {
                                        dark: isDarkMode
                                    })}>{text.title}</h1>
                                </PageTitle>
                            </div>
                        </div>
                    </div>
                    {
                        isFetching ? (
                            <ContentLoader/>
                        ) : (
                            <div className={classNames('content', {
                                dark: isDarkMode
                            })}>
                                {!isTrialPeriod && showUpgradePlan &&
                                    <span className="upgrade-plan">
                                        <WarningIcon/>
                                        <span>{text.inactive_text}</span>
                                        <SmallButton>
                                            <Link to={routes.plans.path}>{text.upgrade_plan}</Link>
                                        </SmallButton>
                                    </span>}
                                {subscriptions?.data ?
                                    subscriptions?.data?.subscriptions?.map(subscription => {
                                        return (
                                            <div className={classNames('subscription', {
                                                dark: isDarkMode
                                            })} key={subscription?.subscription_id}>
                                                <div className="subscription-item-container">
                                                    <div className="subscription-item">
                                                        <span
                                                            className="plan">{`${text.plan}: ${keyToNamePlan(subscription?.plan)}`}</span>
                                                        <div className="labels">
                                                            {!subscription?.can_be_reactivated && !subscription?.payment_pending &&
                                                                <span
                                                                    className={`status ${subscription?.active && !subscription?.payment_failed ? 'active' : 'inactive'}`}>
                                                                    {subscription?.active && !subscription?.payment_failed
                                                                        ? text.active
                                                                        : subscription?.payment_failed ? text.payment_failed : text.inactive
                                                                    }
                                                                </span>
                                                            }
                                                            {(subscription?.active && !subscription?.payment_failed && subscription?.can_be_reactivated && !subscription?.payment_pending) ?
                                                                <>
                                                                    <span
                                                                        className="status canceled">{text.canceled}</span>
                                                                    <div
                                                                        className={`help ${showCanceledInfo === subscription?.subscription_id ? 'active' : ''}`}
                                                                        ref={infoBoxRef}>
                                                                        <span className="icon-container"
                                                                            onClick={() => toggleInfo(subscription?.subscription_id)}>
                                                                            <InfoIcon color={colors.colorBgFilter}/>
                                                                        </span>
                                                                        <InputInfo text={text.canceled_info}/>
                                                                    </div>
                                                                </> :
                                                                subscription?.payment_pending &&
                                                                <>
                                                                    <span
                                                                        className="status canceled">{text.payment_pending}</span>
                                                                    <div
                                                                        className={`help ${showCanceledInfo === subscription?.subscription_id ? 'active' : ''}`}
                                                                        ref={infoBoxRef}>
                                                                        <span className="icon-container"
                                                                            onClick={() => toggleInfo(subscription?.subscription_id)}>
                                                                            <InfoIcon color={colors.colorBgFilter}/>
                                                                        </span>
                                                                        <InputInfo text={text.payment_pending_info}/>
                                                                    </div>
                                                                </>
                                                            }
                                                        </div>
                                                        {Customer.isRoot() && (
                                                            <span className="buttons">
                                                                {(subscription?.active && !subscription?.payment_failed && subscription?.can_be_canceled && !subscription?.payment_pending) ?
                                                                    <SmallButton
                                                                        onClick={() => {
                                                                            dispatch(setConfirmIsActive(true))
                                                                            setDeletingSubscriptionId(subscription?.subscription_id)
                                                                        }}
                                                                        className="bg-red"
                                                                    >
                                                                        {text.cancel}
                                                                    </SmallButton> :
                                                                    (subscription?.active && !subscription?.payment_failed && subscription?.can_be_reactivated && !subscription?.payment_pending) ?
                                                                        <SmallButton
                                                                            onClick={() => activateSubscription(subscription?.subscription_id)}
                                                                        >
                                                                            {processingId === subscription?.subscription_id &&
                                                                            <ButtonLoader/>}
                                                                            {text.reactivate}
                                                                        </SmallButton> :
                                                                        (subscription?.active && subscription?.payment_failed && !subscription?.payment_pending) &&
                                                                    <SmallButton
                                                                        onClick={() => triggerCreateSubscriptionSession(subscription?.subscription_id)}
                                                                    >
                                                                        {text.update_payment}
                                                                    </SmallButton>
                                                                }
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="arrow">
                                                        <span onClick={() => getPayments(subscription?.subscription_id)}
                                                            className={`icon ${paymentId === subscription?.subscription_id ? 'rotate' : ''}`}
                                                        >
                                                            <ArrowDownIcon stroke={colors.colorPrimary} width={'16'} height={'10'}/>
                                                        </span>
                                                    </span>
                                                </div>
                                                <div className="subscription-payments">
                                                    {
                                                        checkIsIdentificContent(subscription?.subscription_id) &&
                                                        (isPaymentFetching ?
                                                            <ContentLoader width='48px' height='48px'/> :
                                                            paymentData?.data ? (
                                                                <Table
                                                                    className="payments-table"
                                                                    tableHeader={tableHeader}
                                                                    data={paymentData?.data?.subscription?.payments}
                                                                    tbodyData={tbodyData}
                                                                    selectedData={paymentData?.data?.subscription?.payments}
                                                                    hasCheckbox={false}
                                                                    isMultiCheckable={false}
                                                                />
                                                            ) : (
                                                                <div
                                                                    className="no-payments">{paymentData?.message}</div>
                                                            ))
                                                    }
                                                </div>
                                            </div>
                                        )
                                    }) : getEmptyContent()
                                }
                            </div>
                        )
                    }
                </div>
                <Footer/>
            </main>
        </>
    )
}

export default Subscriptions
